import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ListItem, Paper, Box, Typography, Button, Avatar, Tooltip, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import MessageAttachments from './MessageAttachments';
import CodeBlock from './CodeBlock';
import SafeMarkdownImage from './SafeMarkdownImage';
import MessageActions from './MessageActions';
import AgentHandoffButton from './AgentHandoffButton';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';
import { formatTimestamp, formatTimestampFull } from '../../utils/timestampFormatter';
import { stripCtaMarker } from '../../utils/ctaMarker';
import { getInitials, getColorFromString } from '../editor/features/roles/utils/rolColorUtils';
import { CHAT_MARKDOWN_TYPOGRAPHY } from './chatTypography';

/**
 * MessageBubble — una burbuja de mensaje ya persistido.
 *
 * Extraído de `ChatMessages` en #930. Antes este JSX vivía inline dentro del
 * `messages.map()`, y por eso React no podía hacer bail-out por mensaje: un
 * único token de streaming re-renderizaba los N mensajes del hilo y cada uno
 * reconstruía un procesador `unified()` para re-parsear su markdown entero.
 *
 * `React.memo` es aquí lo que hace usable un hilo largo, no un adorno. Medido
 * con la cadena real (2026-09-02): un hilo de 128 mensajes / 277 KB cuesta
 * 152 ms por pasada de parseo, y a 30 deltas/s eso pide 4.561 ms de trabajo por
 * segundo sobre un presupuesto de 1.000. El umbral de dolor empieza en ~40
 * mensajes (1.502 ms/s).
 *
 * ⚠️ Para que el memo sirva, TODAS las props deben ser estables. Por eso el
 * componente recibe primitivas y valores ya resueltos en vez de índices y
 * closures: cualquier objeto o función recreada en el padre por render anula el
 * bail-out y devuelve el chat a re-parsearlo todo. El test
 * `ChatMessages.renderCost.test.jsx` vigila justo eso.
 *
 * @param {Object} message - El mensaje. Su identidad la preserva el structural sharing de React Query.
 * @param {Object} theme - Tema MUI, para el contraste WCAG AA de `prepareRoleColorForChat`.
 * @param {Object} colors - `theme.chat`, los tokens de color del chat (nunca hex a pelo, pitfall #8).
 * @param {Object} bubbleMaxWidth - Tope de ancho ya resuelto por el padre (constante de módulo, no un literal por render).
 * @param {boolean} isHighlighted - Destello del deep-link de ejecución (#212).
 * @param {boolean} isAnalyzingAttachment - Muestra el chip «Analizando documento…» en esta burbuja.
 * @param {string} [threadId]
 * @param {boolean} isStoppedPlanAnchor - Esta burbuja ancla el recuento de pasos del ⏹ (#519).
 * @param {string} [stoppedPlanLabel] - Texto ya traducido del recuento de pasos.
 * @param {string} [stoppedPlanExecutionId] - Para el enlace al detalle de la ejecución.
 * @param {string} [executionDetailLabel] - Texto ya traducido del enlace «Ver detalle».
 * @param {boolean} showStopFooter - Pie «⏹ Detenido» colgando del último mensaje (#494 §4.5).
 * @param {boolean} [showStopFooterRetry] - El pie admite RETRY (solo con el corte VIVO, #898).
 * @param {boolean} showRetry - El botón de reintento va en esta burbuja.
 * @param {boolean} isLastMessage - Última respuesta completada (acciones siempre visibles).
 * @param {string} [previousUserMessage] - Contenido del mensaje de usuario anterior, para el feedback.
 */

// Estáticos a nivel de módulo: recrearlos por render era el defecto. `react-markdown`
// no memoiza nada (lib/index.js:163-166), así que un array o un objeto nuevos aquí
// bastan para forzar un re-parseo completo.
const REMARK_PLUGINS = [remarkGfm];
export const MARKDOWN_COMPONENTS = {
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
  code: CodeBlock,
  // #1802: sin esto, `![](https://externo/?d=…)` en una respuesta saca datos al abrir el hilo.
  img: SafeMarkdownImage,
};

const AVATAR_SX = {
  width: { xs: 24, md: 32 },
  height: { xs: 24, md: 32 },
  flexShrink: 0,
  fontWeight: 600,
  fontSize: { xs: '0.75rem', md: '0.875rem' },
  mt: 0.5,
};

const AGENT_NAME_ROW_SX = { display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, ml: 0.5 };
const TIMESTAMP_SX = { color: 'text.disabled', fontSize: { xs: '0.6rem', md: '0.65rem' } };
const CONTENT_WRAPPER_SX = { width: '100%' };
const STOP_SEPARATOR_SX = { display: 'flex', alignItems: 'center', gap: 1, mt: 1, opacity: 0.7 };
const STOP_SEPARATOR_RULE_SX = { flexGrow: 1, height: '1px', bgcolor: 'divider' };
const STOP_SEPARATOR_TEXT_SX = { color: 'text.secondary', whiteSpace: 'nowrap' };
const STOP_FOOTER_TEXT_SX = { color: 'text.secondary' };
const RETRY_BUTTON_SX = { minWidth: 0, py: 0, px: 0.5, fontSize: '0.75rem', textTransform: 'none' };
const DETAIL_LINK_SX = { whiteSpace: 'nowrap' };

// #212: los mensajes del Planner llevan un acento propio, alimentado por un token
// semántico del tema (nunca un hex a pelo, pitfall #8) y validado a WCAG AA.
const isPlannerMessage = (message) => !!message.agentName && /planner/i.test(message.agentName);
const resolveMessageChatColor = (message, theme) =>
  message.color
  || (isPlannerMessage(message) ? theme.palette.primary.main : null)
  || getColorFromString(message.agentName);

const MessageBubble = ({
  message,
  theme,
  colors,
  bubbleMaxWidth,
  isHighlighted,
  isAnalyzingAttachment,
  threadId,
  isStoppedPlanAnchor,
  stoppedPlanLabel,
  stoppedPlanExecutionId,
  executionDetailLabel,
  showStopFooter,
  showStopFooterRetry,
  showRetry,
  isLastMessage,
  previousUserMessage,
  onRetry,
  isRetrying,
  onFeedbackChange,
  onHandoffAction,
}) => {
  const isUser = message.sender === 'usuario';

  // Una sola vez por burbuja. Antes se llamaba DOS veces por mensaje (avatar y
  // nombre) y cada llamada calcula luminancia WCAG + ratio de contraste +
  // getContrastText.
  const roleColors = useMemo(
    () => (isUser ? null : prepareRoleColorForChat(resolveMessageChatColor(message, theme), theme)),
    [isUser, message, theme]
  );

  const content = useMemo(() => stripCtaMarker(message.content), [message.content]);

  const listItemSx = useMemo(() => ({
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    mb: { xs: 0.5, md: 1 },
    display: 'flex',
    alignItems: 'flex-start',
    gap: { xs: 0.75, md: 1 }, // Increased gap between avatar and bubble on mobile
    px: { xs: 2.5, md: 2 }, // Increased mobile padding for better avatar spacing
    // #212: destello del mensaje deep-linkeado desde una ejecución de plan.
    // La transición se declara siempre para que el destello también se apague
    // suave cuando `highlightedMessageId` vuelve a null.
    transition: 'background-color 0.4s ease',
    borderRadius: 1,
    ...(isHighlighted && { bgcolor: 'action.selected' }),
  }), [isUser, isHighlighted]);

  const columnSx = useMemo(() => ({
    display: 'flex',
    flexDirection: 'column',
    ...(isUser
      ? { maxWidth: bubbleMaxWidth, flex: '0 1 auto' }
      // Agente: ancho estable — llena hasta bubbleMaxWidth en vez de ajustarse al
      // contenido, para que la burbuja no "respire" de ancho durante el streaming
      // ni salte de tamaño al persistir el mensaje.
      : { maxWidth: bubbleMaxWidth, flex: '1 1 auto', minWidth: 0 }
    ),
    '&:hover .message-actions': { opacity: 1 },
  }), [isUser, bubbleMaxWidth]);

  const paperSx = useMemo(() => ({
    px: { xs: 1.5, md: 2 },
    py: { xs: 1, md: 1.25 },
    borderRadius: isUser ? '12px 12px 3px 12px' : '3px 12px 12px 12px',
    bgcolor: isUser ? colors.messages.user.background : colors.messages.agent.background,
    color: colors.messages[isUser ? 'user' : 'agent'].text,
    backgroundImage: 'none',
    border: isUser ? 'none' : `1px solid ${colors.messages.agent.borderColor}`,
  }), [isUser, colors]);

  const markdownSx = useMemo(() => ({
    width: '100%',
    // #1116-A: la misma tipografía que la burbuja viva de `ChatMessages`, de una
    // sola fuente — cualquier diferencia entre las dos se ve como un salto al
    // terminar el turno. Ver `chatTypography.js`.
    ...CHAT_MARKDOWN_TYPOGRAPHY,
    backgroundColor: isUser
      ? `${colors.messages.user.background} !important`
      : `${colors.messages.agent.background} !important`,
    '& > *': { margin: '0.5em 0', backgroundColor: 'inherit !important' },
    '& p': { margin: '0.5em 0', backgroundColor: 'inherit !important' },
    '& div': { backgroundColor: 'inherit !important' },
    '& p:first-of-type': { marginTop: 0 },
    '& p:last-child': { marginBottom: 0 },
    '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '0.4em 0', lineHeight: 1.3, fontWeight: 600 },
    '& h1': { fontSize: '1.15em' },
    '& h2': { fontSize: '1.1em' },
    '& h3': { fontSize: '1.05em' },
    '& h4, & h5, & h6': { fontSize: '1em' },
    '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
    '& li': { margin: '0.25em 0' },
    '& span': { backgroundColor: 'inherit' },
    // Code blocks now handled by CodeBlock component
    '& blockquote': { borderLeft: `3px solid ${colors.markdown.blockquote.borderColor}`, paddingLeft: '0.8em', margin: '0.5em 0', color: colors.markdown.blockquote.text },
    '& a': { color: colors.markdown.link.color, textDecoration: colors.markdown.link.decoration },
    '& img': { maxWidth: '100%', borderRadius: '4px' },
    '& table': { display: 'block', overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderCollapse: 'collapse', width: '100%', margin: '0.5em 0', border: `1px solid ${colors.markdown.table.border}`, borderSpacing: 0 },
    '& th, & td': { border: `1px solid ${colors.markdown.table.border}`, padding: '0.4em 0.6em', textAlign: 'left' },
    '& th': { backgroundColor: colors.markdown.table.headerBg, fontWeight: 'bold' },
    '& tr': { borderBottom: `1px solid ${colors.markdown.table.rowBorderStrong}` },
    '& tr:nth-of-type(even)': { backgroundColor: colors.markdown.table.rowBgEven },
    '& tbody': { borderTop: `2px solid ${colors.markdown.table.rowBorderStrong}` },
    '& thead': { borderBottom: `2px solid ${colors.markdown.table.rowBorderStrong}` },
    '& hr': { border: 0, height: '6px', backgroundColor: colors.markdown.hr.color, margin: '1em 0' },
  }), [isUser, colors]);

  const avatarSx = useMemo(() => (roleColors ? {
    background: roleColors.avatarBgGradient,
    boxShadow: roleColors.avatarShadow,
    color: roleColors.avatarText,
    ...AVATAR_SX,
  } : AVATAR_SX), [roleColors]);

  const agentNameSx = useMemo(() => ({
    fontWeight: 600,
    color: roleColors?.nameText,
    fontSize: { xs: '0.7rem', md: '0.75rem' },
  }), [roleColors]);

  const detailLink = stoppedPlanExecutionId ? (
    <Link
      component={RouterLink}
      to={`/executions?execution=${encodeURIComponent(stoppedPlanExecutionId)}`}
      variant="caption"
      sx={DETAIL_LINK_SX}
    >
      {executionDetailLabel}
    </Link>
  ) : null;

  return (
    <ListItem data-message-id={message.id} sx={listItemSx}>
      {/* Avatar FUERA del Paper - solo para agentes a la izquierda */}
      {!isUser && (
        <Avatar sx={avatarSx}>{getInitials(message.agentName || 'A')}</Avatar>
      )}

      {/* Burbuja del mensaje + acciones */}
      <Box sx={columnSx}>

        {/* Nombre del agente con timestamp — fuera del bubble */}
        {!isUser && (
          <Box sx={AGENT_NAME_ROW_SX}>
            <Typography variant="caption" sx={agentNameSx}>
              {message.agentName || 'Agent'}
            </Typography>
            {message.timestamp && (
              <Tooltip title={formatTimestampFull(message.timestamp)} arrow placement="top">
                <Typography variant="caption" sx={TIMESTAMP_SX}>
                  {formatTimestamp(message.timestamp)}
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}

        <Paper
          elevation={0}
          role="article"
          aria-label={!isUser ? `Message from ${message.agentName || 'Agent'}` : 'Your message'}
          sx={paperSx}
        >
          <Box sx={CONTENT_WRAPPER_SX}>
            <Box sx={markdownSx}>
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
                {content}
              </ReactMarkdown>

              {/* Mostrar archivos adjuntos si existen */}
              {message.attachments && message.attachments.length > 0 && (
                <MessageAttachments
                  attachments={message.attachments}
                  threadId={threadId}
                  isAnalyzing={isAnalyzingAttachment}
                />
              )}

              {/* #494 (DEC-1): separador discreto del parcial congelado por un STOP.
                  cancelledByUser sin suppressed_delivery = parcial VISIBLE; los
                  suppressed se filtran en useMessages y nunca llegan aquí. */}
              {!isUser && message.cancelledByUser === true && (
                <Box sx={STOP_SEPARATOR_SX}>
                  <Box sx={STOP_SEPARATOR_RULE_SX} />
                  <Typography variant="caption" sx={STOP_SEPARATOR_TEXT_SX}>
                    {isStoppedPlanAnchor ? `⏹ Detenido — ${stoppedPlanLabel}` : '⏹ Detenido'}
                  </Typography>
                  {isStoppedPlanAnchor && detailLink}
                  <Box sx={STOP_SEPARATOR_RULE_SX} />
                </Box>
              )}
            </Box>
          </Box>
        </Paper>

        {/* #494 (§4.5 fila 2): STOP sin respuesta — en el camino clásico sin
            parcial no hay mensaje del agente donde anclar la marca ni el
            retry, así que el pie cuelga del ÚLTIMO mensaje del hilo, sea de
            quien sea. Se va cuando el usuario actúa: mensaje nuevo, RETRY o
            cambio de hilo limpian `hasStoppedExecution` (#898 — antes lo limpiaba
            también «que el agente se reactivara», y eso lo disparaba solo el motor
            zombi tras el ⏹).

            Ese pie del corte VIVO sigue anclado a la cola porque no tiene turno al
            que agarrarse: un ⏹ sin plan no deja ningún id detrás. El contador de
            pasos SÍ lo tiene y va aparte (#898, `message.stoppedPlan`), así que no
            puede saltar de mensaje.

            Antes exigía que ese último mensaje fuera del usuario, y eso dejaba
            sin aviso el caso peor: medido en prod (2026-08-19), un plan
            cancelado a los 7 s donde el agente ya había dicho «Voy a realizar
            varias búsquedas…» 2 s ANTES del ⏹. Ese mensaje es del asistente y
            no lleva `cancelled_by_user` —solo lo llevan los que llegan DESPUÉS
            del stop, y esos van suprimidos—, así que el usuario se quedaba con
            una promesa de trabajo en curso congelada y sin explicación.

            Se excluye el parcial ya rotulado (`cancelledByUser`): ese lleva su
            propio separador «⏹ Detenido» arriba y duplicaríamos la marca. */}
        {showStopFooter && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 1, mt: 0.5, opacity: 0.8 }}>
            <Typography variant="caption" sx={STOP_FOOTER_TEXT_SX}>
              {/* «sin respuesta» solo es cierto colgando de un mensaje del usuario:
                  si el ancla es del agente, algo dijo (un «voy a…»), lo que faltó
                  fue el resultado. Afirmar lo contrario debajo de su propio texto
                  se contradice con lo que el usuario está leyendo.

                  #519: el contador de pasos DESPLAZA a «sin respuesta». Cuando el
                  turno llevaba un plan, «sin respuesta» es falso en lo que importa
                  —los pasos sí se ejecutaron, con efectos reales; lo que faltó fue
                  el informe— y el recuento dice exactamente eso. */}
              {isStoppedPlanAnchor
                ? `⏹ Detenido — ${stoppedPlanLabel}`
                : (isUser ? '⏹ Detenido — sin respuesta' : '⏹ Detenido')}
            </Typography>
            {isStoppedPlanAnchor && detailLink}
            {/* #898: el pie se pinta por dos motivos —corte vivo o marca histórica
                del plan— y solo el primero admite reintento: una marca que
                sobrevivió a un reload no puede ofrecer reintentar un turno que el
                usuario ya dejó atrás. */}
            {onRetry && showStopFooterRetry && (
              <Button
                size="small"
                variant="text"
                onClick={onRetry}
                disabled={isRetrying}
                sx={RETRY_BUTTON_SX}
              >
                {isRetrying ? 'Reintentando…' : 'Reintentar'}
              </Button>
            )}
          </Box>
        )}

        {/* Cross-agent handoff CTA buttons */}
        {message.actions && message.actions.length > 0 && !isUser && (
          message.actions.map((action, actionIdx) => (
            <AgentHandoffButton
              key={actionIdx}
              label={action.label}
              onClick={() => onHandoffAction?.(action)}
            />
          ))
        )}

        {!isUser && (
          <MessageActions
            message={message}
            threadId={threadId}
            theme={theme}
            previousUserMessage={previousUserMessage}
            onFeedbackChange={onFeedbackChange}
            showRetry={showRetry}
            onRetry={onRetry}
            isRetrying={isRetrying}
            isLastMessage={isLastMessage}
          />
        )}
      </Box>
    </ListItem>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  bubbleMaxWidth: PropTypes.object.isRequired,
  isHighlighted: PropTypes.bool,
  isAnalyzingAttachment: PropTypes.bool,
  threadId: PropTypes.string,
  isStoppedPlanAnchor: PropTypes.bool,
  stoppedPlanLabel: PropTypes.string,
  stoppedPlanExecutionId: PropTypes.string,
  executionDetailLabel: PropTypes.string,
  showStopFooter: PropTypes.bool,
  showStopFooterRetry: PropTypes.bool,
  showRetry: PropTypes.bool,
  isLastMessage: PropTypes.bool,
  previousUserMessage: PropTypes.string,
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
  onFeedbackChange: PropTypes.func,
  onHandoffAction: PropTypes.func,
};

export default React.memo(MessageBubble);
